import React from "react";
import { connect } from "react-redux";
import { Button, Dropdown, Table, Tooltip } from "antd";
import { CheckOutlined, MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import HandledByChip from "../TreatmentPlans/component/shared/HandledByChip";

import { selectActiveJobs } from "../../reducers/scrapeJobs";
import RequestStatusChip from "./RequestStatusChip";
import { OFFICE_STATUSES, formatDay, isAnswer, isFollowUp, outcomeLabel } from "./outcomes";
import { needsSubmissions, requestTypeMeta } from "./requestTypes";
import { actionLabel, primaryAction } from "./requestActions";

/**
 * The requests table — one row per request.
 *
 * Deliberately roomier and quieter than the Patients board's rows:
 * this is read far more often than it is acted on, so the columns carry a
 * primary/secondary pair rather than multiplying into more columns.
 *
 * Live agent runs come from the existing scrapeJobs slice, which is already fed
 * by the office WebSocket mounted once in DashboardLayout — so a row starts
 * pulsing the moment the agent picks the job up, with no polling here.
 */

function fullName(p) {
  return [p.first_name, p.last_name].filter(Boolean).join(" ") || "—";
}

// An actual date, not "a month ago". Relative time reads well for something that
// just happened, but this column is mostly weeks old — and "a month ago" is the
// same string for anything between 26 and 45 days, which is no use when you're
// trying to work out how stale a breakdown is. The tooltip still carries the time.
function when(iso) {
  if (!iso) return "—";
  return dayjs(iso).format("MMM D, YYYY");
}

function items(row) {
  return Array.isArray(row.requested_estimates) ? row.requested_estimates : [];
}

// The earliest scheduled check on the row, with what it is checking for — the
// status line under a Ready row waiting on one ("Next check Oct 3").
function nextFollowUp(row) {
  let next = null;
  items(row).forEach(function (s) {
    if (s.state === "follow_up" && s.follow_up_on && (!next || s.follow_up_on < next.date)) {
      next = { date: s.follow_up_on, outcome: isFollowUp(s.outcome) ? s.outcome : null };
    }
  });
  return next;
}

// What the row's items came to, in words: "3 estimates · 1 covered · 1 not
// covered · 1 awaiting result". The Status column names ONE thing per request
// (the filter it sits under), so on a row with several items this line is where
// the office sees the rest. A single item's answer or next check is already in
// the Status column, so its line only says it is still awaited.
const BUCKET_ORDER = ["covered", "partially_covered", "not_covered", "paid", "done", "waiting", "follow_up", "pending"];

function itemSummary(row, meta) {
  const subs = items(row);
  if (!subs.length) return null;
  const noun = meta.submissionNoun || "estimate";
  const parts = [subs.length === 1 ? "1 " + noun : subs.length + " " + (meta.submissionNounPlural || noun + "s")];
  // On an archived row nothing is awaited any more: an item still without an
  // answer was withdrawn with the request.
  const unanswered = row.status === "archived" ? "not answered" : "awaiting result";
  if (subs.length === 1) {
    if (subs[0].state === "pending" && row.status !== "archived") parts.push(unanswered);
    return parts.join(" · ");
  }
  const tally = {};
  let firstCheck = null;
  subs.forEach(function (s) {
    let bucket;
    if (s.state === "done") bucket = isAnswer(s.outcome) ? s.outcome : "done";
    else if (s.state === "waiting_on_office") bucket = "waiting";
    else if (s.state === "follow_up") {
      bucket = "follow_up";
      if (s.follow_up_on && (!firstCheck || s.follow_up_on < firstCheck)) firstCheck = s.follow_up_on;
    } else bucket = "pending";
    tally[bucket] = (tally[bucket] || 0) + 1;
  });
  BUCKET_ORDER.forEach(function (bucket) {
    const n = tally[bucket];
    if (!n) return;
    if (bucket === "waiting") parts.push(n + " waiting on you");
    else if (bucket === "follow_up") {
      const day = firstCheck ? formatDay(firstCheck, false) : "";
      parts.push(n === 1 ? "1 follow-up " + day : n + " follow-ups (next " + day + ")");
    } else if (bucket === "pending") parts.push(n + " " + unanswered);
    else if (bucket === "done") parts.push(n + " done");
    else parts.push(n + " " + outcomeLabel(bucket).toLowerCase());
  });
  return parts.join(" · ");
}

function RequestsTable({
  rows, loading, activePolicyIds, tab, canManage, canDelete,
  onView, onFix, onReply, onResubmit, onCancel, onDelete, onArchive, onRestore, onSetStatus,
}) {
  // Super admins and covering FTC personnel get `handled_by` on every row; for
  // everyone else the server omits the key entirely, so its absence is what
  // removes the column. Checking the KEY rather than the value matters — a
  // present-but-null value means "nothing has handled this yet", which is a
  // legitimate row for someone who may see the column.
  const showHandledBy = (rows || []).some(function (r) {
    return Object.prototype.hasOwnProperty.call(r || {}, "handled_by");
  });

  // Column widths are PERCENTAGES against a `tableLayout: fixed` table, and Note
  // is the only one without an explicit width — it absorbs whatever is left. That
  // makes the sum load-bearing: adding a column without taking the space from
  // somewhere silently starves Note instead of erroring. It went to 1% when
  // "Handled by" was added, which is why its header rendered one letter per line.
  //
  // So the two layouts declare their own widths, and each leaves Note ~11-14%.
  // Request is NOT trimmed in either: at 21% it has about 14px of slack for
  // "Insurance Breakdown" + the Sooner chip on a 1440 screen, and less puts the
  // urgent rows back to two lines.
  const W = showHandledBy
    ? { patient: "12%", policy: "13%", type: "21%", status: "12%",
        handled: "11%", time: "10%", action: "10%" }   // + Note 11% = 100
    : { patient: "14%", policy: "15%", type: "21%", status: "14%",
        handled: null, time: "11%", action: "11%" };   // + Note 14% = 100

  // The office's own follow-through on a Ready row, set where it shows: the
  // Status cell. The current one is ticked; picking it again changes nothing.
  function statusMenu(row) {
    const options = OFFICE_STATUSES.map(function (key) {
      return {
        key: key,
        label: outcomeLabel(key),
        icon: row.office_status === key ? <CheckOutlined /> : <span className="ir-menu-icon-space" />,
      };
    });
    const menuItems = [{ type: "group", label: "Set status", children: options }];
    if (row.office_status) {
      menuItems.push({ type: "divider" });
      menuItems.push({ key: "clear", label: "Clear status" });
    }
    return {
      items: menuItems,
      onClick: function (info) {
        if (info.key === row.office_status) return;
        onSetStatus(row, info.key === "clear" ? null : info.key);
      },
    };
  }

  const columns = [
    {
      title: "Patient",
      key: "patient",
      // Widths are PERCENTAGES, not pixels, and they deliberately sum to 86% —
      // Note carries the remaining 14%. Percentages because the table must fill
      // the card at every viewport: a pixel budget that fits a 1920 screen
      // overflows a 1440 one (the fixed columns alone came to 1180px against a
      // ~1162px card), and one that fits 1440 leaves a dead gap at 1920.
      //
      // Patient and Policy are sized to their content — a name over a DOB, a
      // carrier over "Primary · <member id>" — rather than left to absorb slack;
      // unconstrained they took roughly a third of the table each and squeezed
      // Request into wrapping.
      width: W.patient,
      render: function (_, row) {
        const dob = row.patient.birth_date;
        return (
          <div>
            <div className="ir-cell-primary">{fullName(row.patient)}</div>
            <div className="ir-cell-secondary">
              {dob ? "DOB " + dayjs(dob).format("MMM D, YYYY") : "No date of birth"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Policy",
      key: "policy",
      width: W.policy,
      render: function (_, row) {
        const p = row.policy || {};
        return (
          <div>
            <div className="ir-cell-primary">{p.carrier_name || "—"}</div>
            <div className="ir-cell-secondary">
              {p.insurance_type ? p.insurance_type.charAt(0).toUpperCase() + p.insurance_type.slice(1) : ""}
              {p.member_id ? " · " + p.member_id : ""}
            </div>
          </div>
        );
      },
    },
    {
      title: "Request",
      key: "type",
      // The widest share, so "Insurance Breakdown" and the "Sooner" chip sit on ONE
      // line even on a 1440 screen (~244px there, against ~230px of content). It
      // used to wrap, which made the urgent rows the tallest ones and drew the eye
      // for entirely the wrong reason.
      width: W.type,
      render: function (_, row) {
        const meta = requestTypeMeta(row.request_type);
        // One row is one policy's request; the items under it (several claims
        // on one policy, say) are its cards in the modal. The line under the
        // type says what each came to — a request completes once every one
        // has its answer.
        // One our FTC team started itself is listed only while it needs the
        // office — say so, or the office wonders when they asked for it.
        const line = [row.initiated_by_ftc ? "Started by our team" : null, itemSummary(row, meta)]
          .filter(Boolean).join(" · ");
        return (
          <div>
            <span className="ir-type">
              {meta.rowLabel}
              {row.urgency === "urgent" ? <span className="ir-urgent">Sooner</span> : null}
            </span>
            {line ? <div className="ir-cell-secondary">{line}</div> : null}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: W.status,
      render: function (_, row) {
        const live = !!(row.policy && activePolicyIds[row.policy.id]);
        const pickable = canManage && row.status === "ready";
        const chip = (
          <RequestStatusChip
            status={row.status}
            readyKey={row.ready_key}
            answer={row.answer}
            isLive={live}
            fellBack={!live}
            isTerminatedPlan={row.is_terminated_plan}
            followUp={nextFollowUp(row)}
            caret={pickable}
          />
        );
        if (!pickable) return chip;
        return (
          <Dropdown trigger={["click"]} menu={statusMenu(row)}>
            <button type="button" className="ir-status-picker" aria-label="Set status">
              {chip}
            </button>
          </Dropdown>
        );
      },
    },
    {
      title: "Note",
      key: "note",
      render: function (_, row) {
        const asked = row.info_ask && !row.info_ask.answered_at ? row.info_ask.summary : "";
        if (!row.note && !row.missing_fields_label && !asked) return <span className="ir-cell-secondary">—</span>;
        // Our team's checklist names what to provide; else the older
        // blank-fields line; else the note.
        const text = asked
          || (row.missing_fields_label ? "Needs " + row.missing_fields_label : row.note);
        return (
          <Tooltip title={text}>
            <div className="ir-note">{text}</div>
          </Tooltip>
        );
      },
    },
    {
      // One column, two questions, because the width budget above has no room for
      // a second person and the two are never both interesting: while a request is
      // being worked you want to know WHO IS ON IT; once something came back, who
      // did it.
      title: tab === "in_progress" ? "Assigned to" : "Handled by",
      key: "handled_by",
      width: W.handled,
      render: function (_, row) {
        if (tab === "in_progress") {
          // Advisory routing: unassigned is an ordinary state (opened before
          // routing existed, or no covering FTC), not a fault.
          if (!row.assigned_to) return <span className="ir-cell-secondary">Unassigned</span>;
          return <span>{row.assigned_to.name}</span>;
        }
        // Null here means "nobody has handled it yet" — an ordinary state for an
        // open request. Matches the Note column's em dash rather than a blank.
        if (!row.handled_by) return <span className="ir-cell-secondary">—</span>;
        return <HandledByChip value={row.handled_by} compact />;
      },
    },
    {
      // Ready is ordered by when value last landed (every new answer moves a
      // request to the top), so that is the date it shows; Archived by when it
      // was put away; In progress by when it was asked for.
      title: tab === "ready" ? "Updated" : tab === "archived" ? "Archived" : "Requested",
      key: "time",
      width: W.time,
      render: function (_, row) {
        let iso = row.created_at;
        if (tab === "ready") iso = row.ready_at || row.completed_at || row.created_at;
        else if (tab === "archived") iso = row.archived_at;
        return (
          <Tooltip title={iso ? dayjs(iso).format("MMM D, YYYY · h:mm A") : ""}>
            <span className="ir-cell-secondary" style={{ marginTop: 0 }}>{when(iso)}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: W.action,
      align: "right",
      render: function (_, row) {
        // The primary action for the row, then the overflow menu. They coexist:
        // a super admin has to be able to delete any row, and the office to
        // archive a Ready one, whatever the primary slot holds.
        const meta = requestTypeMeta(row.request_type);
        // An estimate or claim request has something to look at from the start
        // — its items, and whatever our team has recorded on them so far. A
        // breakdown has nothing to show until it is done.
        const viewable = needsSubmissions(row.request_type)
          ? row.status !== "cancelled"
          : !!row.completed_at;
        const waiting = row.status === "ready" && !!row.waiting_on_office;
        const action = primaryAction(row);
        const needsDetails = action === "fix" || action === "reply";

        let primary = null;
        if (action === "reply") {
          // Our team's Details Needed checklist: the Provide details form
          // answers it field by field and sends the request back.
          primary = (
            <Button size="small" type="primary" ghost onClick={function () { onReply(row); }}>
              Provide details
            </Button>
          );
        } else if (action === "fix") {
          // A flag from before the checklist: blank policy details get the
          // data-entry form (saving it sends the request back).
          primary = (
            <Button size="small" type="primary" ghost onClick={function () { onFix(row); }}>
              Fix details
            </Button>
          );
        } else if (action === "resubmit") {
          // Anything else waiting on the office. An estimate or claim — a claim
          // to send again, a letter only the patient has — opens what our team
          // found ("View Results"); a breakdown on an old flag resubmits.
          primary = (
            <Button size="small" type="primary" ghost onClick={function () { onResubmit(row); }}>
              {actionLabel(row, action)}
            </Button>
          );
        } else if (viewable) {
          primary = (
            <Button size="small" onClick={function () { onView(row); }}>
              {meta.viewAction}
            </Button>
          );
        }

        const menuItems = [];
        if (waiting && viewable) menuItems.push({ key: "view", label: meta.viewAction });
        if (needsDetails) menuItems.push({ key: "resubmit", label: "Resubmit to our team" });
        // Withdrawing is for work still underway. A Ready request is archived
        // instead — which withdraws whatever of it is still open.
        if (row.status === "in_progress") menuItems.push({ key: "cancel", label: "Withdraw request" });
        if (canManage && row.status === "ready") menuItems.push({ key: "archive", label: "Archive" });
        // A withdrawn request (archived while still open) is asked for again,
        // not moved back.
        if (canManage && row.status === "archived" && row.completed_at) {
          menuItems.push({ key: "restore", label: "Move back to Ready" });
        }
        // Super-admin only, and last, behind a divider — destructive and rarely
        // what anyone came here to do.
        if (canDelete) {
          if (menuItems.length) menuItems.push({ type: "divider" });
          menuItems.push({ key: "delete", label: "Delete request", danger: true });
        }

        if (!primary && !menuItems.length) return null;

        return (
          <div className="ir-row-actions">
            {primary}
            {menuItems.length ? (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: menuItems,
                  onClick: function (info) {
                    if (info.key === "delete") onDelete(row);
                    else if (info.key === "view") onView(row);
                    else if (info.key === "resubmit") onResubmit(row);
                    else if (info.key === "archive") onArchive(row);
                    else if (info.key === "restore") onRestore(row);
                    else if (info.key === "cancel") onCancel(row);
                  },
                }}
              >
                <Button size="small" type="text" icon={<MoreOutlined />} aria-label="More actions" />
              </Dropdown>
            ) : null}
          </div>
        );
      },
    },
  ];

  const visibleColumns = columns.filter(function (c) {
    return c.key !== "handled_by" || showHandledBy;
  });

  return (
    <Table
      className="ir-table"
      rowKey="id"
      loading={loading}
      columns={visibleColumns}
      dataSource={rows}
      pagination={false}
      // REQUIRED, not cosmetic. antd defaults to `table-layout: auto`, which sizes
      // the table to its CONTENT — so once the columns got explicit widths, the
      // only width-less one left was Note ("—" on the Ready tab), and the whole
      // table collapsed to the sum of those widths, floating inside a full-width
      // card. With `fixed`, the percentages resolve against the card's own width,
      // so the table spans it at any viewport.
      tableLayout="fixed"
      // The staggered reveal is capped at ten rows: past that the delay stops
      // reading as choreography and starts reading as lag.
      rowClassName={function (_, index) {
        return index < 10 ? `ir-row ir-row-${index}` : "";
      }}
    />
  );
}

const mapStateToProps = function (state) {
  const active = selectActiveJobs(state) || [];
  const byPolicy = {};
  active.forEach(function (job) {
    if (job.policy_id) byPolicy[job.policy_id] = true;
  });
  return { activePolicyIds: byPolicy };
};

export default connect(mapStateToProps)(RequestsTable);
